import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Globe, Palette } from 'lucide-react';

const SettingsGeneral = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('✅ Paramètres enregistrés avec succès');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('❌ Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabinet Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            Informations du Cabinet
          </CardTitle>
          <CardDescription>
            Informations générales sur votre cabinet médical
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cabinet-nom">Nom du Cabinet</Label>
              <Input
                id="cabinet-nom"
                value={settings?.cabinet?.nom || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  cabinet: { ...settings?.cabinet, nom: e.target.value }
                })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="medecin">Médecin Responsable</Label>
              <Input
                id="medecin"
                value={settings?.cabinet?.medecin_responsable || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  cabinet: { ...settings?.cabinet, medecin_responsable: e.target.value }
                })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="adresse">Adresse</Label>
            <Input
              id="adresse"
              value={settings?.cabinet?.adresse || ''}
              onChange={(e) => setSettings({
                ...settings,
                cabinet: { ...settings?.cabinet, adresse: e.target.value }
              })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="telephone">Téléphone</Label>
              <Input
                id="telephone"
                value={settings?.cabinet?.telephone || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  cabinet: { ...settings?.cabinet, telephone: e.target.value }
                })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={settings?.cabinet?.email || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  cabinet: { ...settings?.cabinet, email: e.target.value }
                })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Units Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Globe className="w-5 h-5 mr-2" />
            Unités de Mesure
          </CardTitle>
          <CardDescription>
            Configuration des unités utilisées dans l'application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="longueur">Longueur</Label>
              <select
                id="longueur"
                value={settings?.units?.longueur || 'cm'}
                onChange={(e) => setSettings({
                  ...settings,
                  units: { ...settings?.units, longueur: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="cm">cm</option>
                <option value="in">in</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="poids">Poids</Label>
              <select
                id="poids"
                value={settings?.units?.poids || 'kg'}
                onChange={(e) => setSettings({
                  ...settings,
                  units: { ...settings?.units, poids: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="kg">kg</option>
                <option value="lb">lb</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="temperature">Température</Label>
              <select
                id="temperature"
                value={settings?.units?.temperature || '°C'}
                onChange={(e) => setSettings({
                  ...settings,
                  units: { ...settings?.units, temperature: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="°C">°C</option>
                <option value="°F">°F</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pression">Pression</Label>
              <select
                id="pression"
                value={settings?.units?.pression || 'mmHg'}
                onChange={(e) => setSettings({
                  ...settings,
                  units: { ...settings?.units, pression: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="mmHg">mmHg</option>
                <option value="kPa">kPa</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Theme & Language */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Palette className="w-5 h-5 mr-2" />
            Apparence & Langue
          </CardTitle>
          <CardDescription>
            Personnalisation de l'interface
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="theme">Thème</Label>
              <select
                id="theme"
                value={settings?.theme || 'light'}
                onChange={(e) => setSettings({ ...settings, theme: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="light">Clair</option>
                <option value="dark">Sombre</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="langue">Langue</Label>
              <select
                id="langue"
                value={settings?.langue || 'fr'}
                onChange={(e) => setSettings({ ...settings, langue: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
            color: 'white'
          }}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les Paramètres'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsGeneral;
